import Container from '@/components/atoms/Container'
import Spinner from '@/components/atoms/Spinner'
import { useWatchProfile } from '@/hooks/useWatchProfile'
import onboardingService from '@/services/onboardingService'
import typeformService from '@/services/typeformService'
import { useAppSelector } from '@/store/hooks'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

const getNumberFromScore = (score: number, formId: string): number => {
	const scoreMapping = formId === 'Eh4p88mH' ? [34, 35, 36, 37] : [1, 2, 3, 4]

	if (score >= 0 && score <= 8) {
		return scoreMapping[0]
	} else if (score >= 9 && score <= 22) {
		return scoreMapping[1]
	} else if (score >= 23 && score <= 34) {
		return scoreMapping[2]
	} else if (score >= 35) {
		return scoreMapping[3]
	} else {
		return 0
	}
}

const typeforms: { [key: number]: string } = {
	1: 'YN8t0ft3', // inglês
	2: 'Eh4p88mH', // espanhol
}

export function Body() {
	const {
		matriculate: { classSelect },
		auth: { user },
	} = useAppSelector((state) => state)

	const navigate = useNavigate()
	const [loading, setLoading] = useState(false)
	const [redirecting, setRedirecting] = useState(false) // Novo estado
	const [formId, setFormId] = useState('')
	const [hasSeenResult, setHasSeenResult] = useState(false)

	const { refetch, isLoading: isLoadingProfile } = useWatchProfile()

	const findOnboarding = user?.onboarding_all?.find(
		(onboarding) => onboarding.curso_id === classSelect?.curso_id
	)

	useEffect(() => {
		// Redireciona apenas se o usuário ainda não viu o resultado e o nível está incompleto
		if (
			findOnboarding?.nivel_onboarding !== 2 &&
			!findOnboarding?.proficiencia_id &&
			!hasSeenResult
		) {
			if (user?.modo_curso_unico) navigate('/curso')

			navigate('/')
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [findOnboarding?.nivel_onboarding, hasSeenResult])

	const { execute: nextProgress, isLoading: isLoadingProgress } =
		onboardingService.progressStudent()

	const {
		execute: getResultTypeform,
		data,
		isLoading,
		isSuccess,
		isError,
	} = typeformService.getResultMutation()

	async function handleRedirection(path: string) {
		setRedirecting(true) // Ativa o estado de redirecionamento
		await new Promise((resolve) => setTimeout(resolve, 2000)) // Simula um pequeno atraso
		navigate(path)
	}

	function dispatchNext(proficiencia_id: number) {
		if (!findOnboarding?.nivel_onboarding) {
			navigate('/curso')
			return
		}

		setLoading(true)

		nextProgress(
			{
				step: findOnboarding?.nivel_onboarding,
				curso_id: findOnboarding?.curso_id,
				proficiencia_id,
			},
			async () => {
				await refetch()
				setLoading(false)
				setHasSeenResult(true)
				await handleRedirection('/curso/sucesso-nivelamento')
			}
		)
	}

	useEffect(() => {
		const handleFormSubmit = async ({
			formId,
			responseId,
		}: {
			formId: string
			responseId: string
		}) => {
			if (responseId && Object.values(typeforms).includes(formId)) {
				setLoading(true)
				setFormId(formId)
				await new Promise((resolve) => setTimeout(resolve, 10000))
				await getResultTypeform({ formId, responseId })
			}
		}

		window.submit = handleFormSubmit

		const script = document.createElement('script')
		script.src = 'https://embed.typeform.com/next/embed.js'
		script.async = true

		document.body.appendChild(script)

		return () => {
			document.body.removeChild(script)
			delete window.submit
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		console.log(data)

		if (isSuccess && data?.variables) {
			const score = data?.variables?.find(
				(item: { key: string }) => item.key === 'score'
			)?.number

			if (score && formId && !hasSeenResult) {
				const scoreNumber = parseInt(score.toString(), 10)
				const newProficienciaId = getNumberFromScore(scoreNumber, formId)

				if (newProficienciaId) {
					dispatchNext(newProficienciaId)
				}
			}
		}

		if (isError) handleRedirection('/curso')
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSuccess, isError, data?.variables])

	const selectedTypeform = findOnboarding
		? typeforms[findOnboarding.curso_id]
		: undefined

	if (
		loading ||
		isLoading ||
		isLoadingProgress ||
		isLoadingProfile ||
		redirecting
	) {
		return (
			<Container className='flex flex-col items-center justify-center min-h-[65vh] gap-8 lg:gap-12 z-20'>
				<Spinner
					className={
						'inline w-12 h-12 md:w-14 md:h-14 text-gray-200/20 animate-spin fill-brand-primary'
					}
				/>
				<p className='text-3xl md:text-4xl lg:text-[40px] text-white font-semibold text-center w-full max-w-[465px] leading-[120%]'>
					Carregando o resultado do teste
				</p>
			</Container>
		)
	}

	if (!selectedTypeform) {
		navigate('/curso')
		return null
	}

	return (
		<Container className='flex flex-col items-center justify-center gap-12 py-2 pb-10'>
			<div className='flex flex-col justify-center items-center gap-4'>
				<h1 className='text-4xl text-white font-bold leading-[120%] relative text-center z-10'>
					Realize o Teste de Nivelamento
				</h1>
				<p className='text-white/90 w-full max-w-[578px] text-center'>
					Separe um tempinho para fazer com calma. O resultado é muito
					importante para você descobrir por onde começará sua jornada!
				</p>
			</div>

			<div
				data-tf-widget={selectedTypeform}
				data-tf-on-submit='submit'
				data-tf-on-ending-button-click=''
				className='w-full max-w-screen-lg h-screen max-h-[600px] z-20'
			></div>
		</Container>
	)
}
